/**
 * meta/store.js — the ticket-pack store, as a DEMO stub (GDD 8.3, 12.4).
 *
 * No payment is taken and none can be: `buy()` grants tickets outright and
 * every surface that presents a pack carries `STORE_CONFIG.demoLabel`. This is
 * deliberate — a store that looks real but silently does nothing is worse than
 * one that says what it is.
 *
 * GDD 12.4 requires an IAP master switch for age rating. With
 * `save.settings.iapEnabled === false` the store is not merely disabled, it is
 * *absent*: `packs()` returns nothing, `buy()` refuses, and the UI layer never
 * builds the kiosk. Hiding the entry point is the requirement, not greying it out.
 *
 * @module meta/store
 */

import { TICKET_PACKS, STORE_CONFIG, EVENTS, META_EVENTS } from '../constants.js';
import { economy } from '../core/save.js';
import { bus } from '../core/events.js';
import { logCall } from '../core/testmode.js';
import { iapEnabled, setIapEnabled } from './state.js';

/** Packs a player can actually buy — the Starter ships with the game. */
const PURCHASABLE = TICKET_PACKS.filter((p) => !p.included);

/** @param {object} pack @returns {number} Tickets per pound/dollar. */
function ticketsPerUnit(pack) {
  return pack.price > 0 ? Math.round(pack.tickets / pack.price) : 0;
}

export const store = {
  /** @returns {boolean} GDD 12.4 master switch. */
  isEnabled() {
    return iapEnabled();
  },

  /** @param {boolean} on @returns {boolean} */
  setEnabled(on) {
    const value = setIapEnabled(on);
    logCall('meta', 'store.setEnabled', [value]);
    return value;
  },

  /**
   * The packs on sale. Empty when IAP is switched off, which is what makes the
   * store vanish rather than merely refuse.
   * @returns {Array<object>}
   */
  packs() {
    if (!this.isEnabled()) return [];
    return PURCHASABLE.map((p) => ({
      ...p,
      perUnit: ticketsPerUnit(p),
      priceLabel: `$${p.price.toFixed(2)}`,
      bonusLabel: p.bonusPct ? `+${p.bonusPct}%` : '',
    }));
  },

  /** @param {string} packId @returns {object|null} */
  pack(packId) {
    return this.packs().find((p) => p.id === packId) || null;
  },

  /** @returns {string} The label every store surface must show. */
  demoLabel() {
    return STORE_CONFIG.demoLabel;
  },

  /**
   * The text a confirm dialog puts in front of the player before granting.
   * @param {string} packId
   * @returns {string}
   */
  confirmText(packId) {
    const p = this.pack(packId);
    if (!p) return STORE_CONFIG.demoLabel;
    return `${STORE_CONFIG.demoLabel}\n\nGrant ${p.tickets} tickets (${p.name}, ${p.priceLabel})?`;
  },

  /**
   * "Buy" a pack — grants the tickets immediately, takes no payment.
   * @param {string} packId
   * @returns {{ ok: boolean, reason?: string, tickets?: number, pack?: object }}
   */
  buy(packId) {
    logCall('meta', 'store.buy', [packId]);
    if (!this.isEnabled()) return { ok: false, reason: 'iap-disabled' };

    const pack = this.pack(packId);
    if (!pack) return { ok: false, reason: 'unknown-pack' };

    economy.grant({ tickets: pack.tickets }, `store:${pack.id}`);

    bus.emit(EVENTS.UI_TOAST, {
      text: `${pack.name} — +${pack.tickets} tickets (demo)`,
      kind: 'prize',
      duration: 3200,
    });
    bus.emit(EVENTS.AUDIO_PLAY, { name: 'store-purchase', packId: pack.id });
    bus.emit(META_EVENTS.STORE_PURCHASE, { packId: pack.id, tickets: pack.tickets });

    return { ok: true, tickets: pack.tickets, pack };
  },
};

let installed = false;

/**
 * Nudge toward the store when the player cannot pay for a round.
 * Gentle by design (GDD 8.1 — "the carnival is generous"): one toast, no modal,
 * and nothing at all when IAP is switched off.
 */
export function installStorePrompt() {
  if (installed) return;
  installed = true;
  bus.on(EVENTS.INSUFFICIENT_TICKETS, ({ needed, have }) => {
    if (!store.isEnabled()) return;
    bus.emit(EVENTS.UI_TOAST, {
      text: `${needed} tickets needed, you have ${have} — top up at the entrance kiosk`,
      kind: 'bad',
      duration: 3600,
    });
  });
}

export default store;
